// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Options for formatCurrency.
 * - decimals: number of fraction digits (default 2). Pass 0 for whole-dollar display ($1,234).
 * - includeSymbol: prefix with $. Default true.
 */
export interface FormatCurrencyOptions {
  decimals?: number;
  includeSymbol?: boolean;
  /** ISO 4217 code. Omit to use the session's currency — pass it explicitly
   * where the figure belongs to a SPECIFIC issuer rather than to the signed-in
   * reseller (a parent printing a child's document). */
  currency?: string | null;
}

/**
 * Format a number as US currency with proper thousands separators and decimal places.
 *
 * Backwards-compatible: the second arg can be a boolean (legacy `includeSymbol`) or an options object.
 *
 * @example
 * formatCurrency(10800)                       // "$10,800.00"
 * formatCurrency(10800, false)                // "10,800.00"           (legacy form)
 * formatCurrency(10800, { decimals: 0 })      // "$10,800"
 * formatCurrency(10800, { decimals: 0, includeSymbol: false }) // "10,800"
 */
/**
 * ─────────────────────────────────────────────────────────────────────────
 * ADAPTED FOR THIS PORTAL: the currency symbol is not always "$".
 * ─────────────────────────────────────────────────────────────────────────
 * In xop-edge-staff every figure is XOP's, priced in dollars, so `$` is
 * hardcoded below. Here each reseller trades in its own currency — set on
 * the Branding page — and printing dollars on a euro invoice is wrong in a
 * way the customer will notice.
 *
 * `setPortalCurrency` is called once by `CrmRoot` from the signed-in
 * reseller's branding. Module-level rather than threaded through ~100 call
 * sites because it IS a session-level fact: one reseller, one trading
 * currency.
 *
 * THE EDGE THAT REMAINS, stated rather than hidden: a PARENT reseller
 * viewing a CHILD's document sees the parent's symbol in LIST rows, because
 * a list row is formatted without knowing whose document it is. The document
 * itself is crm-api's rendering (`InvoicePdfRenderer`), formatted in the
 * document's own currency, and that is the artefact the customer receives.
 */
let portalCurrency = 'USD';

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CHF: 'CHF ', SEK: 'kr ', NOK: 'kr ', DKK: 'kr ',
  PLN: 'zł ', CZK: 'Kč ', CAD: 'CA$', AUD: 'A$', NZD: 'NZ$', JPY: '¥', CNY: '¥',
  INR: '₹', BRL: 'R$', ZAR: 'R ', AED: 'AED ', SGD: 'S$', HKD: 'HK$',
};

/** Set the session's trading currency (an ISO 4217 code). Unknown codes fall
 * back to the code itself, which is clearer than the wrong symbol. */
export function setPortalCurrency(code: string | null | undefined): void {
  portalCurrency = (code || 'USD').toUpperCase();
}

export function currencySymbol(code?: string | null): string {
  const c = (code || portalCurrency).toUpperCase();
  return CURRENCY_SYMBOLS[c] ?? `${c} `;
}

/** The session's trading currency as an ISO 4217 CODE, for callers that
 * COMPARE currencies rather than print one — the price-list picker warns when
 * an item is priced in something else (see `@/pages/portal/priceList.ts`). */
export function portalCurrencyCode(): string {
  return portalCurrency;
}

export function formatCurrency(
  amount: number | string | null | undefined,
  optionsOrIncludeSymbol: boolean | FormatCurrencyOptions = true
): string {
  // Normalize args (preserve old `(amount, false)` signature)
  const options: FormatCurrencyOptions =
    typeof optionsOrIncludeSymbol === 'boolean'
      ? { includeSymbol: optionsOrIncludeSymbol }
      : optionsOrIncludeSymbol;
  const decimals = options.decimals ?? 2;
  const includeSymbol = options.includeSymbol ?? true;

  try {
    let numAmount: number;

    if (amount === null || amount === undefined) {
      numAmount = 0;
    } else if (typeof amount === 'string') {
      const cleanedAmount = amount.replace(/[$,\s]/g, '');
      numAmount = parseFloat(cleanedAmount) || 0;
    } else if (typeof amount === 'number') {
      numAmount = amount;
    } else {
      numAmount = 0;
    }

    if (isNaN(numAmount)) numAmount = 0;

    const formatted = numAmount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    return includeSymbol ? `${currencySymbol(options.currency)}${formatted}` : formatted;
  } catch (error) {
    console.error('Error in formatCurrency:', error, 'Input:', amount);
    const sym = currencySymbol(options.currency);
    return includeSymbol
      ? (decimals === 0 ? `${sym}0` : `${sym}0.00`)
      : (decimals === 0 ? '0' : '0.00');
  }
}
